import React, { InputHTMLAttributes } from 'react';
import classNames from 'classnames';
import { RadioButton } from 'components/core';
import formStyles from 'components/core/Form/Form.module.scss';
import { Inline, InlineProps } from 'components/layout';
import { Stack, StackProps } from 'components/layout';

type LayoutProps =
  | { layout: 'Inline'; layoutProps: Omit<InlineProps, 'children'> }
  | { layout: 'Stack'; layoutProps: Omit<StackProps, 'children'> }
  | { layout?: undefined; layoutProps?: undefined };

type Props = {
  ariaDescribedBy?: string;
  isError?: boolean;
  size?: 'sm' | 'md' | 'lg';
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>;

type LabelProps =
  | {
      // Accessible name for the group when it has no visible label
      ariaLabel: string;
      ariaLabelledBy?: never;
    }
  | {
      // Id of the group's visible label, eg: a FormLabels `id`. Use this
      // instead of ariaLabel when there is a visible label
      ariaLabelledBy: string;
      ariaLabel?: never;
    };

export const RadioGroup = ({
  children,
  className,
  defaultValue = '',
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  layout,
  layoutProps,
  onChange,
  isError,
  'aria-describedby': formControlDescribedBy,
  'aria-errormessage': formControlErrorId,
  'aria-invalid': ariaInvalid,
  ...props
}: Props & LabelProps & LayoutProps) => {
  const [selectedValue, setSelectedValue] = React.useState(defaultValue);

  // The group's own description plus FormControl's helper text and actions
  const describedByIds = [ariaDescribedBy, formControlDescribedBy].filter(
    Boolean
  );

  // FormControl sets these when it has an error; pass them on to every radio
  const radioErrorProps = isError
    ? {
        isError,
        'aria-invalid': true,
        'aria-errormessage': formControlErrorId,
      }
    : {};

  // Recursively enhance RadioButton components at any nesting level
  const enhanceChildren = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;

      if (child.type === RadioButton) {
        const childProps = { ...props };
        // Remove aria-label accidentally passed to RadioGroup instead of ariaLabel
        delete childProps['aria-label'];

        return React.cloneElement<any>(child, {
          onSelected: (value: string) => setSelectedValue(value),
          selectedValue,
          ...radioErrorProps,
          ...childProps,
          // Pass onChange to the radios only. If the group div had it too, it would
          // fire a second time when the click event bubbles up from the radio.
          ...(onChange ? { onChange } : {}),
        });
      }

      // If the child has children, recursively enhance them
      if (child.props && child.props.children) {
        return React.cloneElement<any>(child, {
          children: enhanceChildren(child.props.children),
        });
      }

      return child;
    });
  };

  const childrenWithProps = enhanceChildren(children);

  return (
    <div
      role="radiogroup"
      className={classNames(formStyles.FormControl, className)}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={describedByIds.join(' ') || undefined}
      aria-invalid={ariaInvalid}
      {...props}
    >
      {layout === 'Inline' ? (
        <Inline {...(layoutProps as InlineProps)}>{childrenWithProps}</Inline>
      ) : layout === 'Stack' ? (
        <Stack {...(layoutProps as StackProps)}>{childrenWithProps}</Stack>
      ) : (
        childrenWithProps
      )}
    </div>
  );
};
